import { format } from "date-fns";
import React, { FC, useEffect, useMemo, useState } from "react";
import { BsArchive } from "react-icons/bs";
import {
  LuAlertTriangle,
  LuPause,
  LuPencil,
  LuPlus,
  LuRepeat,
} from "react-icons/lu";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { SettingsConfirmModal } from "../../../../components/modal/SettingsConfirmModal";
import { WorkModel } from "../../../../factories/works/models/work.model";
import { useWorksSearchParams } from "../../../../hooks/useWorksSearchParams";
import { useAppSelector } from "../../../../redux/redux";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import {
  useArchiveOrUnarchiveMutation,
  useGetRepeatsWorkQuery,
  useGetWorkQuery,
  useStopRecurringMutation,
  useUpdateWorkRepeatsMutation,
} from "../../../../slices/WorkSlice";
import { repeatTypeEnumNever } from "../../../../types/types";
import {
  AiRepeatState,
  repeatFormForWork,
} from "../../../ai/ask/actionProposal.utils";
import { WorkList } from "../../../works/components/WorkList";
import {
  workTableHeaderItems,
  WorkTableSort,
} from "../../../works/types/work-table";
import {
  describeFrequency,
  getFormFromSchedule,
  RecurringScheduleForm,
  RepeatWorkPayload,
} from "./recurringSchedule";
import { RecurringScheduleModal } from "./RecurringScheduleModal";
import { repeatListRows } from "./seriesStatus";

const formatDate = (value: string | Date) =>
  format(new Date(value), "MMM d, yyyy");

// Every row here is part of the series, so the recurring-icon column is left out.
const REPEAT_LIST_COLUMNS = workTableHeaderItems.filter(
  (item) => item.sort !== WorkTableSort.Repeats,
);
export const WorkRepeats: FC = () => {
  const { user } = useAppSelector((state) => state.appReducer);
  const { workId } = useParams();

  const { sortBy, sortOrder, setSort } = useWorksSearchParams({
    defaultSortBy: WorkTableSort.StartDate,
    defaultSortOrder: "desc",
  });

  const [isStopRecurringConfirmOpen, setIsStopRecurringConfirmOpen] =
    useState(false);
  const [selectedWorks, setSelectedWorks] = useState<WorkModel[]>([]);
  const [scheduleModal, setScheduleModal] = useState<"start" | "edit" | null>(
    null,
  );

  const {
    data: seriesRows,
    isFetching: isLoadingWorks,
    refetch: refetchSeries,
  } = useGetRepeatsWorkQuery({
    orgId: getLinkedFirm()?.orgId || "",
    workId: workId || "",
    currentUserId: user.id,
    sortBy,
    sortOrder,
  });
  // The tab badge counts these same rows (useWorkTabCounts).
  const works = useMemo(
    () => repeatListRows(seriesRows, workId) ?? [],
    [seriesRows, workId],
  );
  const { data: work, refetch: refetchWork } = useGetWorkQuery({
    workId: workId || "",
  });
  // Set right after "Stop recurring" succeeds, so the banner changes at once
  // (QA batch 11: it kept "Recurring schedule active" until a reload, because
  // the list's refetch could still carry the schedule). Cleared when a
  // schedule is started/edited or another work item is opened.
  const [stoppedHere, setStoppedHere] = useState<string | null>(null);

  const [archiveOrUnarchive, { isLoading: isArchiving }] =
    useArchiveOrUnarchiveMutation();
  const [stopRecurring, { isLoading: isStoppingRecurring }] =
    useStopRecurringMutation();
  const [updateWorkRepeats, { isLoading: isUpdatingRepeats }] =
    useUpdateWorkRepeatsMutation();
  const orgId = getLinkedFirm()?.orgId || "";

  const hasSelectedWorks = selectedWorks.length > 0;

  // The newest work item in the series carries the schedule; older copies keep
  // their old repeat type after it is stopped. "On completion" copies start on
  // the day the previous one was completed, so order by due date first.
  const seriesByDue = useMemo(
    () =>
      [...works].sort(
        (a, b) =>
          new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime() ||
          new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
      ),
    [works],
  );
  const latestWork = seriesByDue[seriesByDue.length - 1];
  const isRecurring =
    stoppedHere !== workId &&
    (latestWork
      ? !!latestWork.repeatType && latestWork.repeatType !== "Never"
      : !!work &&
        work.repeatTypeEnumId?.toLowerCase() !== repeatTypeEnumNever.id &&
        !work.stopRecurring);
  const scheduleAnchor = latestWork
    ? { startDate: latestWork.startDate, dueDate: latestWork.dueDate }
    : work
    ? { startDate: work.startDate, dueDate: work.dueDate }
    : null;

  // Ask ProCharted "repeat this work item …": the schedule window opens
  // pre-filled once the series is loaded (Edit flow when it already repeats);
  // nothing changes until Save.
  const location = useLocation();
  const navigate = useNavigate();
  const [aiRepeat, setAiRepeat] = useState(
    () => (location.state as AiRepeatState | null)?.aiRepeat ?? null,
  );
  const [aiForm, setAiForm] = useState<RecurringScheduleForm | null>(null);
  useEffect(() => {
    if ((location.state as AiRepeatState | null)?.aiRepeat)
      navigate(location.pathname + location.search, {
        replace: true,
        state: null,
      });
  }, [location.state, location.pathname, location.search, navigate]);
  useEffect(() => {
    if (!aiRepeat || isLoadingWorks || !work || !scheduleAnchor) return;
    setAiForm(repeatFormForWork(aiRepeat, scheduleAnchor));
    setScheduleModal(isRecurring ? "edit" : "start");
    setAiRepeat(null);
  }, [aiRepeat, isLoadingWorks, work, scheduleAnchor, isRecurring]);
  useEffect(() => {
    if (!scheduleModal) setAiForm(null);
  }, [scheduleModal]);

  const handleStartSchedule = async (payload: RepeatWorkPayload) => {
    const targetWorkId = latestWork?.workId || workId;
    if (!targetWorkId) return;

    try {
      await updateWorkRepeats({ workId: targetWorkId, ...payload }).unwrap();
      setStoppedHere(null);
      setScheduleModal(null);
      toast.success("Recurring schedule started");
    } catch {
      toast.error("Couldn't start the recurring schedule. Please try again.");
    }
  };

  // Editing replaces the schedule: stop it on the newest work item, then start
  // the new settings from there. Changing a running schedule in place re-arms
  // older copies on the backend and creates duplicates.
  const handleEditSchedule = async (payload: RepeatWorkPayload) => {
    const targetWorkId = latestWork?.workId || workId;
    if (!targetWorkId) return;

    try {
      await stopRecurring({ jobId: targetWorkId, orgId }).unwrap();
    } catch {
      toast.error("Couldn't update the recurring schedule. Please try again.");
      return;
    }
    try {
      await updateWorkRepeats({ workId: targetWorkId, ...payload }).unwrap();
      setStoppedHere(null);
      setScheduleModal(null);
      toast.success("Recurring schedule updated");
    } catch {
      setScheduleModal(null);
      toast.error(
        "The old schedule was stopped, but the new one couldn't be saved. Please start it again.",
      );
    }
  };

  const editForm =
    scheduleModal === "edit" && work && scheduleAnchor
      ? getFormFromSchedule(
          {
            repeatTypeEnumId: work.repeatTypeEnumId,
            repeatValue: latestWork?.repeats ?? work.repeatValue,
            repeatOnId: work.repeatOnId,
            repeatUntil: work.repeatUntil,
            beforeStartDateValue: work.beforeStartDateValue,
          },
          scheduleAnchor,
        )
      : undefined;
  // The list shows every row (no pages), so the heading counts what is listed.
  const totalRecords = works.length;

  const handleArchive = async () => {
    if (!hasSelectedWorks) return;

    await archiveOrUnarchive({
      body: selectedWorks.map((i) => i.workId),
      archiveWork: true,
    }).unwrap();

    setSelectedWorks([]);
  };

  const handleStopRecurring = async () => {
    if (!workId || !work) return;

    await stopRecurring({ jobId: workId, orgId }).unwrap();
    await updateWorkRepeats({
      workId,
      repeatTypeEnumId: repeatTypeEnumNever.id,
      beforeDays: work.beforeDays,
      repeatOnId: work.repeatOnId,
      repeatValue: (work.repeatValue || 1).toString(),
      repeatUntil: work.repeatUntil ? new Date(work.repeatUntil) : new Date(),
      dueDate: new Date(work.dueDate).toISOString(),
      beforeStartDate: work.beforeStartDate,
      beforeStartDateValue: work.beforeStartDateValue,
    }).unwrap();
    setStoppedHere(workId);
    setIsStopRecurringConfirmOpen(false);
    toast.success("Recurring work stopped successfully");
    // Both calls are done now: read the list and the work item again.
    void refetchSeries();
    void refetchWork();
  };

  return (
    <div>
      {work && (
        <div
          className={
            isRecurring
              ? "mb-4 flex items-center gap-3.5 rounded-[13px] border border-[#DDD5FA] bg-[#F6F4FF] px-5 py-4"
              : "mb-4 flex items-center gap-3.5 rounded-[13px] border border-[#E5E7EC] bg-[#FBFBFC] px-5 py-4"
          }>
          <span
            className={
              isRecurring
                ? "flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] bg-[#EAE4FF] text-[#6B4EFF]"
                : "flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] bg-[#EEF0F3] text-[#8A90A2]"
            }>
            {isRecurring ? <LuRepeat size={18} /> : <LuPause size={18} />}
          </span>
          <div className={"min-w-0 flex-1"}>
            <div
              className={
                "flex items-center gap-2 text-[14.5px] font-extrabold text-[#1F2433]"
              }>
              {isRecurring && (
                <span className={"h-2 w-2 rounded-full bg-[#22C55E]"} />
              )}
              {isRecurring
                ? "Recurring schedule active"
                : "This work doesn't repeat"}
            </div>
            <div className={"mt-0.5 text-[13.5px] text-[#6B7185]"}>
              {isRecurring ? (
                <span className={"text-[#4A5568]"}>
                  Repeats{" "}
                  <b className={"font-bold"}>
                    {describeFrequency(
                      latestWork?.repeatType,
                      latestWork?.repeats,
                    )}
                  </b>
                  {seriesByDue[0] && (
                    <>
                      , first occurrence{" "}
                      <b className={"font-bold"}>
                        {formatDate(seriesByDue[0].startDate)}
                      </b>
                    </>
                  )}
                  ,{" "}
                  {work.repeatUntil ? (
                    <>
                      until{" "}
                      <b className={"font-bold"}>
                        {formatDate(work.repeatUntil)}
                      </b>
                    </>
                  ) : (
                    <b className={"font-bold"}>no end date</b>
                  )}
                  .
                </span>
              ) : works.length > 0 ? (
                "No new work items will be created. The occurrences below were created while the schedule was active."
              ) : (
                "This work isn’t on a recurring schedule. Start one to have ProCharted create it automatically on a cadence you choose."
              )}
            </div>
          </div>
          {isRecurring ? (
            <div className={"flex shrink-0 items-center gap-2"}>
              <button
                type={"button"}
                disabled={!scheduleAnchor}
                onClick={() => setScheduleModal("edit")}
                className={
                  "flex items-center gap-1.5 rounded-lg border border-[#DDD5FA] bg-white px-4 py-[9px] text-[13px] font-bold text-[#6B4EFF] transition-colors hover:bg-[#F6F4FF] disabled:cursor-not-allowed disabled:opacity-60"
                }>
                <LuPencil size={14} strokeWidth={2.4} />
                Edit schedule
              </button>
              <button
                type={"button"}
                onClick={() => setIsStopRecurringConfirmOpen(true)}
                className={
                  "rounded-lg border border-[#E7BEBE] bg-white px-4 py-[9px] text-[13px] font-bold text-[#D14343] transition-colors hover:bg-[#FDF3F3]"
                }>
                Stop recurring
              </button>
            </div>
          ) : (
            <button
              type={"button"}
              disabled={!scheduleAnchor}
              onClick={() => setScheduleModal("start")}
              className={
                "flex shrink-0 items-center gap-1.5 rounded-lg bg-[#6B4EFF] px-4 py-[9px] text-[13px] font-bold text-white shadow-[0_2px_8px_rgba(107,78,255,0.28)] transition-colors hover:bg-[#5A3EEB] disabled:cursor-not-allowed disabled:opacity-60"
              }>
              <LuPlus size={15} strokeWidth={2.6} />
              Start recurring schedule
            </button>
          )}
        </div>
      )}

      <WorkList
        works={works}
        isLoading={isLoadingWorks || isArchiving}
        archiveWorks={selectedWorks}
        setArchiveWorks={setSelectedWorks}
        hasPages={false}
        page={1}
        totalPages={1}
        onPageChange={() => {}}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={setSort}
        currentWorkId={workId}
        headerItems={REPEAT_LIST_COLUMNS}
        emptyState={
          <div
            className={
              "flex flex-col items-center border-t border-[#ECEEF2] px-6 py-14 text-center"
            }>
            <span
              className={
                "mb-4 flex h-[52px] w-[52px] items-center justify-center rounded-[13px] bg-[#F1F2F5] text-[#A6ACBB]"
              }>
              <LuRepeat size={24} strokeWidth={1.8} />
            </span>
            <div
              className={"mb-1.5 text-[15.5px] font-extrabold text-[#1F2433]"}>
              No occurrences yet
            </div>
            <div
              className={
                "max-w-[360px] text-[13.5px] leading-[1.55] text-[#6B7185]"
              }>
              Each work item created from a recurring schedule will be listed
              here.
            </div>
          </div>
        }>
        <div
          className={
            "flex w-full items-center justify-between px-[32px] py-[24px] font-bold"
          }>
          <div>All Work ({totalRecords})</div>

          <div className={"flex items-center gap-4"}>
            <button
              type={"button"}
              disabled={!hasSelectedWorks || isArchiving}
              onClick={() => void handleArchive()}
              className={"disabled:cursor-not-allowed disabled:opacity-40"}>
              <BsArchive
                data-tooltip-id={"tooltip-archive"}
                color={"#7c66f0"}
                size={"20px"}
                className={"flex items-center focus:outline-0 active:outline-0"}
              />
            </button>
          </div>
        </div>
      </WorkList>

      {scheduleModal && scheduleAnchor && (
        <RecurringScheduleModal
          work={scheduleAnchor}
          initialForm={aiForm ?? editForm}
          isDraft={!!aiForm && scheduleModal === "start"}
          isLoading={isUpdatingRepeats || isStoppingRecurring}
          onClose={() => setScheduleModal(null)}
          onSave={(payload) =>
            void (scheduleModal === "edit"
              ? handleEditSchedule(payload)
              : handleStartSchedule(payload))
          }
        />
      )}

      {isStopRecurringConfirmOpen && (
        <SettingsConfirmModal
          onClose={() => setIsStopRecurringConfirmOpen(false)}
          onConfirm={() => void handleStopRecurring()}
          icon={<LuAlertTriangle size={24} />}
          title={"End Task recurrences?"}
          description={
            "This will stop all future recurrences but will not affect the current series of work items."
          }
          confirmText={"Continue"}
          variant={"primary"}
          iconVariant={"warning"}
          isLoading={isStoppingRecurring || isUpdatingRepeats}
        />
      )}
    </div>
  );
};
